import moment from "moment";
import { getApi, postApi } from "utils/apiCall";

/**
 * Demo dashboard data.
 *
 * Scoped to the demo accounts below so every other user keeps hitting the
 * live API. `getDashboardApi` is a drop-in replacement for `getApi` inside
 * the dashboard widgets: for a demo account it answers the dashboard
 * endpoints locally, for anyone else it just delegates.
 */

// Each entry is one login (user id + client id, as stored at login).
const DEMO_ACCOUNTS = [
    { userId: "2310", clientId: "5" }, // Jason Jack
    { userId: "2326", clientId: "9" }, // Demo Administrator (demo.admin)
];

// Simulated network latency so the widgets still show their loaders.
const DEMO_LATENCY_MS = 350;

export const isDemoDashboardUser = () => {
    const userId = localStorage.getItem("user_id");
    const clientId = localStorage.getItem("client_id");
    return DEMO_ACCOUNTS.some((account) => account.userId === userId && account.clientId === clientId);
};

const FACILITIES = [
    { name: "MACG Boronia", state: "Victoria", laundry: true },
    { name: "MACG Carrum Downs", state: "Victoria", laundry: true },
    { name: "MACG Casey Aged Care", state: "Victoria", laundry: true },
    { name: "MACG Casey Manor", state: "Victoria", laundry: true },
    { name: "MACG Cheltenham Manor", state: "Victoria", laundry: false },
    { name: "MACG Grand Cedar", state: "Victoria", laundry: true },
    { name: "MACG Templestowe", state: "Victoria", laundry: false },
    { name: "MACG Parramatta", state: "New South Wales", laundry: true },
    { name: "MACG Newcastle", state: "New South Wales", laundry: true },
    { name: "MACG Brisbane North", state: "Queensland", laundry: true },
    { name: "MACG Adelaide Hills", state: "South Australia", laundry: false },
    { name: "MACG Canberra Central", state: "Australian Capital Territory", laundry: true },
];

const TOTAL_FACILITIES = FACILITIES.length;
const LAUNDRY_FACILITIES = FACILITIES.filter((f) => f.laundry).length;
const ALL_FACILITY_NAMES = FACILITIES.map((f) => f.name).join(",");

// Stable pseudo-random offset so a given month always renders the same numbers.
const seed = (value) => {
    let total = 0;
    for (let i = 0; i < value.length; i++) {
        total = (total * 31 + value.charCodeAt(i)) % 997;
    }
    return total;
};

// -1 = past month, 0 = current month, 1 = future month.
const monthPosition = (date) => {
    const selected = moment(date, "YYYY-MM").startOf("month");
    const current = moment().startOf("month");
    if (!selected.isValid()) return 0;
    if (selected.isBefore(current)) return -1;
    if (selected.isAfter(current)) return 1;
    return 0;
};

const ok = (result) => ({
    error: false,
    result: { success: true, message: "Records are available.", result },
});

/* ------------------------------------------------------------------ *
 * Periodicals & Work Scheduler
 * ------------------------------------------------------------------ */

const periodicalCount = (date) => {
    const jitter = seed(date) % 7;
    const position = monthPosition(date);

    let total;
    let scheduled;
    let assigned;
    let inProgress;
    let completed;
    let overdue;

    if (position < 0) {
        // Closed month — nearly everything is done, a few slipped through.
        total = 54 + jitter;
        scheduled = 0;
        assigned = 0;
        inProgress = 0;
        overdue = 2 + (jitter % 3);
        completed = total - overdue;
    } else if (position === 0) {
        // Month in flight — a mix across every status.
        total = 58 + jitter;
        completed = 27 + (jitter % 5);
        inProgress = 6;
        assigned = 9;
        overdue = 2;
        scheduled = total - completed - inProgress - assigned - overdue;
    } else {
        // Not started yet — everything sits in Scheduled.
        total = 48 + jitter;
        scheduled = total;
        assigned = 0;
        inProgress = 0;
        completed = 0;
        overdue = 0;
    }

    return ok({
        status_counts: [
            { status_id: 1, status_name: "Scheduled", count: scheduled },
            { status_id: 2, status_name: "Assigned", count: assigned },
            { status_id: 3, status_name: "In Progress", count: inProgress },
            { status_id: 5, status_name: "Completed", count: completed },
            { status_id: 6, status_name: "Overdue", count: overdue },
            { status_id: 7, status_name: "All", count: total },
        ],
        total_count: total,
        period: date,
    });
};

/* ------------------------------------------------------------------ *
 * Cleaning / Laundry audits
 * ------------------------------------------------------------------ */

const cleaningAuditReport = (date) => {
    const jitter = seed(date) % 5;
    const position = monthPosition(date);

    let completed;
    let rectificationNeeded;
    let rectificationCompleted;

    if (position < 0) {
        completed = TOTAL_FACILITIES;
        rectificationNeeded = jitter % 2;
        rectificationCompleted = 3 + (jitter % 3);
    } else if (position === 0) {
        completed = 8 + (jitter % 3);
        rectificationNeeded = 2;
        rectificationCompleted = 3;
    } else {
        completed = 0;
        rectificationNeeded = 0;
        rectificationCompleted = 0;
    }

    const pending = TOTAL_FACILITIES - completed;

    return ok([
        {
            rectification_needed: rectificationNeeded,
            rectification_completed: rectificationCompleted,
            completed,
            total: TOTAL_FACILITIES,
            pending,
            total_facilities: TOTAL_FACILITIES,
        },
    ]);
};

const laundryAuditReport = (date) => {
    const jitter = seed(date) % 4;
    const position = monthPosition(date);

    let completed;
    let rectificationPending;
    let rectificationCompleted;

    if (position < 0) {
        completed = LAUNDRY_FACILITIES;
        rectificationPending = 0;
        rectificationCompleted = 2 + (jitter % 2);
    } else if (position === 0) {
        completed = 6 + (jitter % 2);
        rectificationPending = 1;
        rectificationCompleted = 2;
    } else {
        completed = 0;
        rectificationPending = 0;
        rectificationCompleted = 0;
    }

    const pending = LAUNDRY_FACILITIES - completed;

    return ok([
        {
            rectification_pending: rectificationPending,
            rectification_completed: rectificationCompleted,
            audit_completed: completed,
            total_count: LAUNDRY_FACILITIES,
            audit_pending: pending,
            total_facilities: LAUNDRY_FACILITIES,
        },
    ]);
};

/* ------------------------------------------------------------------ *
 * Staff by state
 * ------------------------------------------------------------------ */

const userDetails = () =>
    ok([
        { state_id: 2, state_name: "Victoria", floater_staff_count: 6, manager_count: 5, staff_count: 34, total_users: 45 },
        { state_id: 1, state_name: "New South Wales", floater_staff_count: 4, manager_count: 3, staff_count: 18, total_users: 25 },
        { state_id: 3, state_name: "Queensland", floater_staff_count: 2, manager_count: 2, staff_count: 11, total_users: 15 },
        { state_id: 36, state_name: "Western Australia", floater_staff_count: 1, manager_count: 1, staff_count: 4, total_users: 6 },
        { state_id: 5, state_name: "South Australia", floater_staff_count: 2, manager_count: 1, staff_count: 7, total_users: 10 },
        { state_id: 35, state_name: "Tasmania", floater_staff_count: 0, manager_count: 1, staff_count: 3, total_users: 4 },
        { state_id: 6, state_name: "Australian Capital Territory", floater_staff_count: 1, manager_count: 1, staff_count: 6, total_users: 8 },
    ]);

/* ------------------------------------------------------------------ *
 * Equipment
 * ------------------------------------------------------------------ */

const equipment = (facility, name, serial, testTag, expiry, status) => ({
    equipment_id: serial,
    equipment: name,
    serial_number: serial,
    test_tag_date: testTag,
    expiry_date: expiry,
    status,
    other_details: { facility_name: facility },
});

const equipmentDetails = () =>
    ok([
        // Expired
        equipment("MACG Boronia", "Floor Scrubber – Tennant T300", "EQ-1042", "18-06-2025", "18-06-2026", "Expired"),
        equipment("MACG Carrum Downs", "Vacuum – Nilfisk VP300", "EQ-1088", "02-07-2025", "02-07-2026", "Expired"),
        equipment("MACG Newcastle", "Washer Extractor – Electrolux W575H", "EQ-1131", "11-07-2025", "11-07-2026", "Expired"),
        // Expiring soon
        equipment("MACG Casey Manor", "Carpet Extractor – Polivac C25", "EQ-1015", "05-08-2025", "05-08-2026", "Expiring Soon"),
        equipment("MACG Grand Cedar", "Tumble Dryer – Dexter T-400", "EQ-1067", "21-08-2025", "21-08-2026", "Expiring Soon"),
        equipment("MACG Parramatta", "Steam Cleaner – Duplex 340", "EQ-1109", "29-08-2025", "29-08-2026", "Expiring Soon"),
        equipment("MACG Brisbane North", "Auto Scrubber – Karcher B40", "EQ-1156", "03-09-2025", "03-09-2026", "Expiring Soon"),
        // Up to date
        equipment("MACG Boronia", "Washer Extractor – Speed Queen SC40", "EQ-1003", "14-01-2026", "14-01-2027", "Up to date"),
        equipment("MACG Carrum Downs", "Tumble Dryer – Speed Queen ST050", "EQ-1021", "14-01-2026", "14-01-2027", "Up to date"),
        equipment("MACG Casey Aged Care", "Floor Polisher – Polivac PV25", "EQ-1034", "02-02-2026", "02-02-2027", "Up to date"),
        equipment("MACG Casey Aged Care", "Vacuum – Pacvac Superpro 700", "EQ-1035", "02-02-2026", "02-02-2027", "Up to date"),
        equipment("MACG Casey Manor", "Washer Extractor – Electrolux W555H", "EQ-1049", "27-02-2026", "27-02-2027", "Up to date"),
        equipment("MACG Cheltenham Manor", "Auto Scrubber – Tennant T2", "EQ-1072", "16-03-2026", "16-03-2027", "Up to date"),
        equipment("MACG Grand Cedar", "Carpet Extractor – Britex Nugget", "EQ-1081", "16-03-2026", "16-03-2027", "Up to date"),
        equipment("MACG Templestowe", "Vacuum – Nilfisk GD5", "EQ-1094", "08-04-2026", "08-04-2027", "Up to date"),
        equipment("MACG Parramatta", "Washer Extractor – IPSO IY135", "EQ-1112", "08-04-2026", "08-04-2027", "Up to date"),
        equipment("MACG Newcastle", "Floor Scrubber – Karcher BD 43", "EQ-1128", "22-05-2026", "22-05-2027", "Up to date"),
        equipment("MACG Brisbane North", "Tumble Dryer – Huebsch HT075", "EQ-1149", "22-05-2026", "22-05-2027", "Up to date"),
        equipment("MACG Adelaide Hills", "Vacuum – Pacvac Velo", "EQ-1163", "10-06-2026", "10-06-2027", "Up to date"),
        equipment("MACG Canberra Central", "Washer Extractor – Electrolux W5105H", "EQ-1177", "10-06-2026", "10-06-2027", "Up to date"),
        equipment("MACG Canberra Central", "Auto Scrubber – Tennant T300e", "EQ-1178", "24-06-2026", "24-06-2027", "Up to date"),
    ]);

/* ------------------------------------------------------------------ *
 * Certificates
 * ------------------------------------------------------------------ */

const certificateDetails = () =>
    ok([
        {
            type_id: 1,
            certificate_type: "Public Liability",
            grouped_client_id: null,
            total_certificate_count: 2,
            expired_count: 0,
            certificate_details: [
                {
                    name: "Public Liability – $20,000,000 (Allianz)",
                    expiry_date: "30-06-2027",
                    pdf_link: "demo/certificates/public-liability-2027.pdf",
                },
                {
                    name: "Products Liability – $20,000,000 (Allianz)",
                    expiry_date: "30-06-2027",
                    pdf_link: "demo/certificates/products-liability-2027.pdf",
                },
            ],
        },
        {
            type_id: 2,
            certificate_type: "WorkCover",
            grouped_client_id: null,
            total_certificate_count: 5,
            expired_count: 1,
            certificate_details: [
                { state_name: "Victoria", expiry_date: "31-08-2026", pdf_link: "demo/certificates/workcover-vic.pdf" },
                { state_name: "New South Wales", expiry_date: "30-09-2026", pdf_link: "demo/certificates/workcover-nsw.pdf" },
                { state_name: "Queensland", expiry_date: "31-12-2026", pdf_link: "demo/certificates/workcover-qld.pdf" },
                { state_name: "South Australia", expiry_date: "31-03-2027", pdf_link: "demo/certificates/workcover-sa.pdf" },
                { state_name: "Australian Capital Territory", expiry_date: "30-06-2026", pdf_link: "demo/certificates/workcover-act.pdf" },
            ],
        },
        {
            type_id: 3,
            certificate_type: "ISO",
            grouped_client_id: null,
            total_certificate_count: 3,
            expired_count: 0,
            certificate_details: [
                { name: "ISO 9001:2015 – Quality Management", expiry_date: "14-02-2027", pdf_link: "demo/certificates/iso-9001.pdf" },
                { name: "ISO 14001:2015 – Environmental Management", expiry_date: "14-02-2027", pdf_link: "demo/certificates/iso-14001.pdf" },
                { name: "ISO 45001:2018 – Occupational Health & Safety", expiry_date: "09-11-2026", pdf_link: "demo/certificates/iso-45001.pdf" },
            ],
        },
        {
            type_id: 4,
            certificate_type: "CM3",
            grouped_client_id: null,
            total_certificate_count: 1,
            expired_count: 0,
            certificate_details: [
                {
                    name: "CM3 Contractor Prequalification",
                    facility_name: ALL_FACILITY_NAMES,
                    expiry_date: "31-05-2027",
                    pdf_link: "demo/certificates/cm3.pdf",
                },
            ],
        },
        {
            type_id: 5,
            certificate_type: "Labour Hire",
            grouped_client_id: null,
            total_certificate_count: 3,
            expired_count: 1,
            certificate_details: [
                { state_name: "Victoria", expiry_date: "12-04-2027", pdf_link: "demo/certificates/labour-hire-vic.pdf" },
                { state_name: "Queensland", expiry_date: "28-02-2027", pdf_link: "demo/certificates/labour-hire-qld.pdf" },
                { state_name: "South Australia", expiry_date: "19-05-2026", pdf_link: "demo/certificates/labour-hire-sa.pdf" },
            ],
        },
        {
            type_id: 6,
            certificate_type: "Police Check",
            total_certificate_count: 12,
            expired_count: 2,
            total_fac_users: 15,
            certificate_details: [
                { user_id: 2310, name: "Jason Jack", facility_name: ALL_FACILITY_NAMES, expiry_date: "18-03-2027", pdf_link: "demo/police/2310.pdf" },
                { user_id: 2311, name: "Luke Boustead", facility_name: "MACG Boronia,MACG Carrum Downs,MACG Casey Aged Care,MACG Casey Manor", expiry_date: "02-12-2026", pdf_link: "demo/police/2311.pdf" },
                { user_id: 2312, name: "John Citizen", facility_name: "MACG Boronia,MACG Carrum Downs,MACG Casey Aged Care", expiry_date: "27-01-2027", pdf_link: "demo/police/2312.pdf" },
                { user_id: 2318, name: "Hiren Soni", facility_name: "MACG Boronia", expiry_date: "05-06-2026", pdf_link: "demo/police/2318.pdf" },
                { user_id: 2320, name: "Cleaner Boronia", facility_name: "MACG Boronia", expiry_date: "21-09-2026", pdf_link: "demo/police/2320.pdf" },
                { user_id: 2331, name: "Amara Okafor", facility_name: "MACG Cheltenham Manor,MACG Grand Cedar", expiry_date: "14-10-2026", pdf_link: "demo/police/2331.pdf" },
                { user_id: 2334, name: "Daniel Whitmore", facility_name: "MACG Templestowe", expiry_date: "30-04-2027", pdf_link: "demo/police/2334.pdf" },
                { user_id: 2339, name: "Priya Raghavan", facility_name: "MACG Parramatta,MACG Newcastle", expiry_date: "11-07-2026", pdf_link: "demo/police/2339.pdf" },
                { user_id: 2342, name: "Marco Bellini", facility_name: "MACG Newcastle", expiry_date: "08-02-2027", pdf_link: "demo/police/2342.pdf" },
                { user_id: 2347, name: "Sarah Nguyen", facility_name: "MACG Brisbane North", expiry_date: "25-11-2026", pdf_link: "demo/police/2347.pdf" },
                { user_id: 2351, name: "Tom Hargreaves", facility_name: "MACG Adelaide Hills", expiry_date: "16-05-2027", pdf_link: "demo/police/2351.pdf" },
                { user_id: 2356, name: "Elena Petrova", facility_name: "MACG Canberra Central", expiry_date: "03-08-2026", pdf_link: "demo/police/2356.pdf" },
            ],
            certificate_not_uploaded: [
                { user_id: 2358, name: "Maria Citizen", facility_name: "MACG Boronia" },
                { user_id: 2361, name: "Kwame Mensah", facility_name: "MACG Casey Manor,MACG Cheltenham Manor" },
                { user_id: 2364, name: "Riley Thompson", facility_name: "MACG Parramatta" },
            ],
        },
    ]);

/* ------------------------------------------------------------------ *
 * Education & training
 * ------------------------------------------------------------------ */

const EDUCATION_USER_COUNT = 113;

const educationRow = (type, submitted) => ({
    education_type: type,
    submission_count: submitted,
    submitted_user_ids: [],
    total_user_count: EDUCATION_USER_COUNT,
    submission_percentage: ((submitted / EDUCATION_USER_COUNT) * 100).toFixed(1),
    missing_user_count: EDUCATION_USER_COUNT - submitted,
});

const educationCount = () =>
    ok([
        educationRow("Monthly Education", 96),
        educationRow("Tool Box Education", 78),
        educationRow("Competencies", 104),
        educationRow("Memo", 88),
    ]);

/* ------------------------------------------------------------------ *
 * Communication
 * ------------------------------------------------------------------ */

const communicationCount = (date) => {
    const jitter = seed(date) % 6;
    const position = monthPosition(date);

    let completed;
    let outstanding;

    if (position < 0) {
        completed = 38 + jitter;
        outstanding = jitter % 3;
    } else if (position === 0) {
        completed = 24 + jitter;
        outstanding = 7;
    } else {
        completed = 0;
        outstanding = 0;
    }

    return ok({
        status_breakdown: [
            { status_id: 1, status_name: "Outstanding", count: outstanding },
            { status_id: 2, status_name: "Completed", count: completed },
        ],
        total_communications: completed + outstanding,
        facility_count: TOTAL_FACILITIES,
        period: date,
    });
};

/* ------------------------------------------------------------------ *
 * Facility list & status
 * ------------------------------------------------------------------ */

// The four facilities carrying open items in the demo dataset.
const FACILITIES_NEEDING_ATTENTION = [
    "MACG Carrum Downs",
    "MACG Cheltenham Manor",
    "MACG Newcastle",
    "MACG Adelaide Hills",
];

const groupFacilityList = () =>
    ok(
        FACILITIES.map((facility, index) => ({
            facility_id: 5100 + index,
            facility_name: facility.name,
            state_name: facility.state,
            client_id: Number(localStorage.getItem("client_id")), // the logged-in demo account's client
            is_active: 1,
        }))
    );

const facilityStatus = () =>
    ok(
        FACILITIES.map((facility, index) => ({
            facility_id: 5100 + index,
            facility_name: facility.name,
            status: !FACILITIES_NEEDING_ATTENTION.includes(facility.name),
        }))
    );

/* ------------------------------------------------------------------ *
 * Dispatch
 * ------------------------------------------------------------------ */

const dateOf = (url) => {
    const match = url.match(/[?&]date=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : moment().format("YYYY-MM");
};

const resolveDemoResponse = (url) => {
    const path = url.split("?")[0];

    if (path.includes("client_periodical_count")) return periodicalCount(dateOf(url));
    if (path.includes("client_cleaning_audit_report")) return cleaningAuditReport(dateOf(url));
    if (path.includes("client_laundry_audit_report")) return laundryAuditReport(dateOf(url));
    if (path.includes("client_user_details")) return userDetails();
    if (path.includes("get_equipment_details_by_client")) return equipmentDetails();
    if (path.includes("get_certificate_details_by_client")) return certificateDetails();
    if (path.includes("get_education_count_by_client")) return educationCount();
    if (path.includes("client_communication_count")) return communicationCount(dateOf(url));
    if (path.includes("client_group_facility_list")) return groupFacilityList();
    if (path.includes("facility_status")) return facilityStatus();

    return null;
};

const withLatency = async (response) => {
    await new Promise((resolve) => setTimeout(resolve, DEMO_LATENCY_MS));
    return response;
};

/**
 * Drop-in replacement for `getApi` used by the dashboard widgets.
 * Returns canned demo data for the demo account, otherwise calls the real API.
 */
export const getDashboardApi = async (url) => {
    if (isDemoDashboardUser()) {
        const demoResponse = resolveDemoResponse(url);
        if (demoResponse) return withLatency(demoResponse);
    }
    return getApi(url);
};

/** Drop-in replacement for `postApi` used by the dashboard widgets. */
export const postDashboardApi = async (url, data) => {
    if (isDemoDashboardUser()) {
        const demoResponse = resolveDemoResponse(url);
        if (demoResponse) return withLatency(demoResponse);
    }
    return postApi(url, data);
};
